import { html, css, unsafeCSS, LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import { localized } from '@lit/localize';

import { Variant, RoundtripOfferViewModel, SalesSiteResponse } from './types/booking-request.types.js';
import { updateLocale } from './localization/config.js';

import { renderBookingRequestConfirmationTemplate } from './templates/booking-request-confirmation.js';
import { renderBookingRequestFormTemplate } from './templates/booking-request-form.js';

import './components/sales-sites/index.js';

import { withOpportunities } from './utils/opportunity-mixin.js';
import { emitError, emitWarning } from './utils/form-associated.js';
import mainStyles from './styles/main.scss?inline';

enum VIEW_MODE {
    DEFAULT = 'default',
    CONFIRMATION = 'confirmation',
}

type OpportunityPaxPayload = {
    adults: { count: number };
    children: { count: number; age: Array<number | string> };
};

type ValidatableFormControl = HTMLElement & {
    checkValidity?: () => boolean;
    reportValidity?: () => boolean;
};

type HttpStatusError = Error & {
    status: number;
};

const isHttpStatusError = (error: unknown): error is HttpStatusError =>
    error instanceof Error &&
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof error.status === 'number';

/**
 * Roundtrip booking request web component.
 *
 * Responsibilities:
 * - Loads brand theme assets and applies the current locale.
 * - Renders either the request form or a confirmation view.
 * - Collects and validates form data, then submits it to the CRM endpoint.
 * - Emits analytics and view-mode change events.
 */
@localized()
export class RoundtripBookingRequest extends withOpportunities(LitElement) {
    static override styles = [
        css`
            ${unsafeCSS(mainStyles)}
        `,
    ];

    // static formAssociated = true;
    // private _internals!: ElementInternals;

    @property({ type: String })
    public lang = '';

    @property({ type: String })
    public currency = '';

    @property({ type: String })
    public brand = '';

    @property({ type: String })
    public tenant = '';

    @property({ type: Object, attribute: 'additional-tracking-data' })
    public additionalTrackingData = {};

    @property({ type: Boolean, attribute: 'confirmation' })
    confirmation: boolean = false;

    @property({ type: String, attribute: 'contact-language' })
    contactLanguage: string = '';

    @property({ type: String, attribute: 'form-name' })
    formName: string = 'roundtripBookingForm';

    @property({ type: String, attribute: 'identifier' })
    identifier: string = '';

    @property({ type: Object, attribute: 'offer-data' })
    offerData: RoundtripOfferViewModel = {
        uri: '',
        price: {
            amount: 0,
            formattedString: '',
            formattedStringWithCurrency: '',
            bestPrice: false,
            currency: '',
        },
        travellers: {
            adults: 1,
            children: 0,
        },
        startDate: '',
        endDate: '',
        duration: 0,
    };

    @property({ type: String, attribute: 'product-key' })
    productKey: string = '';

    @property({ type: String, attribute: 'trip-title' })
    tripTitle: string = '';

    @property({ type: String, attribute: 'variant' })
    variant: Variant = Variant.Default;

    @property({ type: String, attribute: 'newsletter' })
    newsletter = '';

    @property({ type: String, attribute: 'support-phone' })
    supportPhone = '';

    @property({ type: String, attribute: 'facet_audience' })
    public facetAudience = '';

    @property({ type: String, attribute: 'facet_style' })
    public facetStyle = '';

    @property({ type: String, attribute: 'facet_motive' })
    public facetMotive = '';

    @state()
    viewMode: VIEW_MODE = VIEW_MODE.DEFAULT;

    @state()
    adultPaxCount: number = 2;

    @state()
    childrenPaxCount: number = 0;

    @state()
    salesSite: SalesSiteResponse = {} as SalesSiteResponse;

    @state()
    salesSiteIdentifier: string | undefined = undefined;

    @state()
    protected isSubmitting: boolean = false;

    @state()
    protected missingInternalValueError: boolean = false;

    override opportunityOriginatingObjectTag: string = 'der-tailormade-booking-request';

    private _currentBrand: string | null = null;

    private readonly emailPattern = '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$';

    private readonly namePattern = "^(?!\\s+$)[A-Za-zÀ-ÖØ-öø-ÿ'\\- ]+$";

    private _lastOpportunityPax: OpportunityPaxPayload | null = null;

    private _buildOpportunityPaxPayload(formData: FormData): OpportunityPaxPayload {
        const adultsRaw = formData.get('OpportunityPaxAdult');
        const childrenRaw = formData.get('OpportunityPaxChildren');

        const adultsParsed = parseInt(String(adultsRaw ?? '1'), 10);
        const childrenParsed = parseInt(String(childrenRaw ?? '0'), 10);

        const adultsCount = Number.isFinite(adultsParsed) ? Math.min(9, Math.max(1, adultsParsed)) : 1;
        const childrenCount = Number.isFinite(childrenParsed) ? Math.min(9, Math.max(0, childrenParsed)) : 0;

        const childrenAges: Array<number | string> = [];
        for (let i = 1; i <= childrenCount; i++) {
            const raw = formData.get(`OpportunityChildrenAge${i}`);
            const rawValue = raw == null ? '' : String(raw);
            const asNumber = Number(rawValue);
            childrenAges.push(Number.isFinite(asNumber) && rawValue !== '' ? asNumber : rawValue);
        }

        return {
            adults: { count: adultsCount },
            children: { count: childrenCount, age: childrenAges },
        };
    }

    /**
     * Lit lifecycle hook.
     *
     * Initializes localization for the configured language.
     */
    async connectedCallback(): Promise<void> {
        super.connectedCallback();
        const langAttribute = this.lang ?? 'de-CH';
        await updateLocale(langAttribute);

        this._currentBrand = this.brand || null;
    }

    /**
     * Lit lifecycle hook.
     *
     * Reacts to runtime changes:
     * - Updates localization when `lang` changes.
     * - Tracks brand changes.
     * - Seeds and refreshes sales site details when the owning team changes.
     *
     * @param changedProperties Map of changed properties with their previous values.
     */
    override async updated(changedProperties: Map<string, unknown>): Promise<void> {
        super.updated(changedProperties);

        if (changedProperties.has('confirmation')) {
            const nextViewMode = this.confirmation ? VIEW_MODE.CONFIRMATION : VIEW_MODE.DEFAULT;

            if (this.viewMode !== nextViewMode) {
                this.viewMode = nextViewMode;
            }
        }

        // Handle language changes at runtime
        if (changedProperties.has('lang')) {
            const newLang = this.lang ?? 'de';
            console.log(`Language changed to: ${newLang}`);
            await updateLocale(newLang);
            this.requestUpdate();
        }

        // Handle brand changes at runtime
        if (changedProperties.has('brand') && this.brand !== this._currentBrand) {
            console.log(`🔄 Brand changed from "${this._currentBrand}" to "${this.brand}"`);
            this._currentBrand = this.brand;
        }

        // Seed selected sales site from preset owning team.
        if (changedProperties.has('opportunityOwningTeam') && this.opportunityOwningTeam) {
            this.salesSiteIdentifier = this.opportunityOwningTeam;
        }

        // When selection or locale changes, refetch details for confirmation view.
        if (
            (changedProperties.has('salesSiteIdentifier') || changedProperties.has('lang')) &&
            this.salesSiteIdentifier
        ) {
            await this._getSalesSiteById();
        }
    }

    /**
     * Emit a warning event.
     * @param name - Warning name
     * @param data - Warning data
     */
    protected warn(name: string, data: object): void {
        emitWarning(this, name, data);
    }

    /**
     * Emit an error event.
     * @param name - Error name
     * @param data - Error data
     */
    protected error(name: string, data: object): void {
        emitError(this, name, data);
    }

    /**
     * Handles the sales site change event.
     * @param event Sales site selection event from dropdown component.
     * @private
     */
    private _handleSalesSiteChange(event: CustomEvent): void {
        event.stopPropagation();
        this.salesSiteIdentifier = event.detail.salesSite;
    }

    /**
     * Builds the base URL for the sales-site API.
     * @returns Base URL for the sales-sites API.
     */
    private _getSalesSitesBaseUrl(): URL {
        return new URL(import.meta.env.VITE_SALES_SITES_URL);
    }

    /**
     * Fetches sales site data by ID.
     * @private
     */
    private async _getSalesSiteById(): Promise<void> {
        const url: URL = this._getSalesSitesBaseUrl();
        url.searchParams.set('teamId', this.salesSiteIdentifier ?? '');
        url.searchParams.set('lang', this.lang ?? '');

        const response: Response = await fetch(url.toString());
        if (!response.ok) {
            this.error(this.tagName, {
                message: 'Can not load sales-site data',
                teamId: this.salesSiteIdentifier,
            });
        }

        this.salesSite = await response.json();
    }

    /**
     * Scrolls to the confirmation section smoothly after a short delay.
     */
    scrollToConfirmation(): void {
        setTimeout(() => {
            this.shadowRoot
                ?.querySelector('.m-booking-request__confirmation')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    }

    /**
     * Updates the contact language based on the current language setting.
     */
    _updateContactLanguage(): void {
        const languageMap: { [key: string]: string } = {
            'fr-CH': '948150001',
            'de-CH': '948150000',
        };
        this.contactLanguage = this.contactLanguage || languageMap[this.lang] || '';
    }

    /**
     * Updates the opportunity source URL to the current page URL if not already set.
     */
    _updateOpportunitySourceUrl(): void {
        this.opportunitySourceUrl = this.opportunitySourceUrl || (top || window)?.location?.href || '';
    }

    /**
     * Syncs the adult pax count with the counter component.
     * @param e Counter change event.
     */
    onAdultCounterChange(e: CustomEvent<{ count: number }>): void {
        const value = Number(e.detail?.count);
        this.adultPaxCount = Number.isFinite(value) ? Math.min(9, Math.max(1, value)) : 1;
    }

    /**
     * Syncs the children pax count with the counter component.
     * @param e Counter change event.
     */
    onChildrenCounterChange(e: CustomEvent<{ count: number }>): void {
        const value = Number(e.detail?.count);
        this.childrenPaxCount = Number.isFinite(value) ? Math.min(9, Math.max(0, value)) : 0;
    }

    /**
     * Handles the click event on the back button to navigate back to the offer.
     *
     * Emits `der.confirmation-close` so the host application can navigate back.
     *
     * @param e Click event.
     */
    onBackButtonClick(e: Event): void {
        e.preventDefault();
        e.stopPropagation();

        const opportunityPax: OpportunityPaxPayload =
            this._lastOpportunityPax ??
            ({
                adults: { count: this.adultPaxCount },
                children: { count: this.childrenPaxCount, age: [] as Array<number | string> },
            } as OpportunityPaxPayload);

        const opportunityPaxTotal = String(opportunityPax.adults.count + opportunityPax.children.count);

        /**
         * Signal the hosting environment to close the confirmation view.
         */
        this.dispatchEvent(
            new CustomEvent('confirmation-close', {
                detail: {
                    offerData: this.offerData,
                    productKey: this.productKey,
                    identifier: this.identifier,
                    opportunityPax,
                    opportunityPaxTotal,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Sends an analytics event upon form confirmation.
     * @param data Analytics payload.
     */
    confirmationAnalyticsEvent(data: unknown): void {
        this.dispatchEvent(
            new CustomEvent('der-analytics', {
                detail: {
                    name: 'form_submitted',
                    element_name: this.tagName.toLowerCase(),
                    element_id: this.id ?? '',
                    action: 'request confirmation view',
                    category: 'roundtrip offer request',
                    data: data,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    private getValidatableControlsInDomOrder(form: HTMLFormElement): ValidatableFormControl[] {
        const selector = [
            'input[name]',
            'select[name]',
            'textarea[name]',
            'der-ui-radio-group[name]',
            'der-ui-input[name]',
            'der-ui-phone-input[name]',
            'der-ui-dropdown[name]',
            'der-ui-checkbox[name]',
            'der-tm-roundtrip-booking-request-sales-sites[name]',
        ].join(',');

        return Array.from(form.querySelectorAll<ValidatableFormControl>(selector)).filter((control) => {
            if (control instanceof HTMLInputElement && control.type === 'hidden') {
                return false;
            }

            return !control.hasAttribute('disabled');
        });
    }

    private reportFormValidityInDomOrder(form: HTMLFormElement): boolean {
        for (const control of this.getValidatableControlsInDomOrder(form)) {
            if (!control.checkValidity || control.checkValidity()) {
                continue;
            }

            return control.reportValidity?.() ?? false;
        }

        return true;
    }

    /**
     * Handles the form submission event.
     *
     * Collects `FormData`, enriches it with opportunity context, performs a minimal
     * validity check, emits analytics events, and submits to the configured CRM endpoint.
     *
     * @param e Submit event.
     */
    async onSubmitClick(e: SubmitEvent): Promise<void> {
        e.preventDefault();
        if (this.isSubmitting) {
            return;
        }
        try {
            const form = e.currentTarget as HTMLFormElement;
            await this.updateComplete;
            const formData = new FormData(form);
            const isSpamSubmission = String(formData.get('ContactEmailConfirmation') ?? '').trim() !== '';

            if (isSpamSubmission) {
                return;
            }

            const costsConfirmation = this.shadowRoot?.querySelector(
                'der-ui-checkbox[name="OpportunityCostConfirmation"]'
            ) as (HTMLElement & { reportValidity?: () => boolean }) | null;

            const formValid = this.reportFormValidityInDomOrder(form);
            if (!formValid) {
                return;
            }

            const childrenAgeValues = Array.from({ length: this.childrenPaxCount }, (_, index) =>
                String(formData.get(`OpportunityChildrenAge${index + 1}`) ?? '').trim()
            );
            const firstEmptyChildrenAgeIndex = childrenAgeValues.findIndex((value) => value === '');

            if (firstEmptyChildrenAgeIndex !== -1) {
                const firstEmptyChildrenAgeDropdown = this.shadowRoot?.querySelector(
                    `der-ui-dropdown[name="OpportunityChildrenAge${firstEmptyChildrenAgeIndex + 1}"]`
                ) as (HTMLElement & { reportValidity?: () => boolean }) | null;
                firstEmptyChildrenAgeDropdown?.reportValidity?.();
                return;
            }

            const costsConfirmationValid =
                this.variant !== Variant.Costs ||
                ((costsConfirmation?.reportValidity?.() ?? true) && formData.has('OpportunityCostConfirmation'));
            if (!costsConfirmationValid) {
                return;
            }

            this.isSubmitting = true;
            const formValues = Object.fromEntries(formData.entries());
            delete formValues.ContactNewsletter;
            const isNewsletterChecked = formData.has('ContactNewsletter');
            this._updateContactLanguage();
            this._updateOpportunitySourceUrl();

            const opportunityPax = this._buildOpportunityPaxPayload(formData);
            this._lastOpportunityPax = opportunityPax;

            // Avoid sending both the structured payload and the legacy flat fields for ages.
            const sanitizedFormValues = { ...formValues } as Record<string, unknown>;
            for (let i = 1; i <= opportunityPax.children.count; i++) {
                delete sanitizedFormValues[`OpportunityChildrenAge${i}`];
            }

            delete sanitizedFormValues['OpportunityPaxAdult'];
            delete sanitizedFormValues['OpportunityPaxChildren'];

            const totalPax = opportunityPax.adults.count + opportunityPax.children.count;
            const opportunityDescription = this.opportunityDescription.trim();
            const newsletter = (this.newsletter ?? '').trim();

            // Check if alle required values that the CRM expects are set
            if (
                this.opportunityTitle.trim() === '' ||
                this.opportunityType.trim() === '' ||
                sanitizedFormValues['ContactFirstName'] === '' ||
                sanitizedFormValues['ContactLastName'] === '' ||
                sanitizedFormValues['ContactLastName'] === '' ||
                sanitizedFormValues['ContactGender'] === ''
            ) {
                this.missingInternalValueError = true;
                return;
            }

            const json: Record<string, unknown> = {
                formName: this.formName,
                ContactLanguage: this.contactLanguage,
                brand: this.brand,
                OpportunityOwningTeam: this.opportunityOwningTeam,
                OpportunityDescription: opportunityDescription,
                OpportunityDestination: this.opportunityDestination,
                OpportunityProductBrand: this.opportunityProductBrand,
                OpportunityProductId: this.opportunityProductId,
                OpportunitySource: this.opportunitySource,
                OpportunitySourceUrl: this.opportunitySourceUrl,
                OpportunityTitle: this.opportunityTitle,
                OpportunityType: this.opportunityType,
                OpportunityOriginatingObjectTag: this.opportunityOriginatingObjectTag,
                offerData: this.offerData,
                productKey: this.productKey,
                identifier: this.identifier,
                variant: this.variant,
                ContactNewsletterOptIn: isNewsletterChecked ? newsletter : '',
                ...sanitizedFormValues,
                OpportunityPax: String(totalPax),
                OpportunityPaxPayload: opportunityPax,
            };

            const analyticsData = {
                position: 'roundtrip',
                language: this.lang,
                ...json,
            };

            const confirmationAnalyticsData = {
                position: 'roundtrip',
                language: this.lang,
                productId: this.opportunityProductId,
                productKey: this.productKey ?? '',
                name: this.opportunityTitle,
                email: String(json['ContactEmail'] ?? ''),
                trackingData: this.additionalTrackingData,
            };

            this.dispatchEvent(
                new CustomEvent('der-analytics', {
                    detail: {
                        name: 'form_submit',
                        element_name: this.tagName.toLowerCase(),
                        element_id: this.id ?? '',
                        action: 'request form submit clicked',
                        category: 'roundtrip offer request',
                        data: analyticsData,
                    },
                    bubbles: true,
                    composed: true,
                })
            );
            /**
             * Emit the full submission payload for integrators.
             */
            this.dispatchEvent(
                new CustomEvent('form-submitted', {
                    detail: json,
                    bubbles: true,
                    composed: true,
                })
            );

            const url = new URL(import.meta.env.VITE_CRM_URL);

            fetch(url, {
                method: 'POST',
                body: JSON.stringify(json),
                headers: { 'Content-Type': 'application/json' },
            })
                .then((response) => {
                    if (response.ok) {
                        this._changeViewMode(VIEW_MODE.CONFIRMATION);
                        this.confirmationAnalyticsEvent(confirmationAnalyticsData);
                        this.scrollToConfirmation();
                    } else {
                        const error: HttpStatusError = Object.assign(new Error(`HTTP-Fehler: ${response.status}`), {
                            status: response.status,
                        });
                        throw error;
                    }
                })
                .catch((error: unknown) => {
                    console.log('Error in form submission:', error);
                    if (isHttpStatusError(error)) {
                        if (error.status !== 200) {
                            this.missingInternalValueError = true;
                            // add some sort of logging
                        }
                        return;
                    }
                    this.isSubmitting = false;
                })
                .finally(() => {
                    // Unlock after async completes
                    if (this.missingInternalValueError) return;
                    this.isSubmitting = false;
                });
        } catch (error) {
            this.error(this.tagName, {
                message: 'Error during form submission: ' + error,
                teamId: this.salesSiteIdentifier,
            });
            this.isSubmitting = false;
        }
    }

    /**
     * Changes the view mode of the component.
     * @param mode The target view mode to display.
     * @private
     */
    private _changeViewMode(mode: VIEW_MODE) {
        this.viewMode = mode;
        this.confirmation = mode === VIEW_MODE.CONFIRMATION;
        /**
         * Inform listeners that the component switched its internal view.
         */
        this.dispatchEvent(
            new CustomEvent('der.view-mode-change', {
                detail: {
                    mode,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Main render function.
     *
     * Renders either the form view or the confirmation view depending on `viewMode`.
     *
     * @returns Lit template for the component.
     */
    override render() {
        const startDateLabel = formatDateToDDMMYY(this.offerData?.startDate ?? '');
        const endDateLabel = formatDateToDDMMYY(this.offerData?.endDate ?? '');
        const priceLabel = this.offerData?.price?.formattedStringWithCurrency ?? '';
        const hasTripInfo = Boolean(this.tripTitle && startDateLabel && endDateLabel && priceLabel);
        const newsletter = (this.newsletter ?? '').trim();
        const supportPhone = (this.supportPhone ?? '').trim();

        return html`
            ${this.viewMode === VIEW_MODE.DEFAULT
                ? html`
                      <div class="m-booking-request">
                          ${renderBookingRequestFormTemplate({
                              formName: this.formName,
                              onSubmit: this.onSubmitClick,
                              variant: this.variant,
                              hasTripInfo,
                              tripTitle: this.tripTitle,
                              startDateLabel,
                              endDateLabel,
                              priceLabel,
                              lang: this.lang,
                              newsletter,
                              supportPhone,
                              namePattern: this.namePattern,
                              emailPattern: this.emailPattern,
                              hasOwningTeam: Boolean(this.opportunityOwningTeam),
                              onSalesSiteSelected: this._handleSalesSiteChange,
                              isSubmitting: this.isSubmitting,
                              adultPaxCount: this.adultPaxCount,
                              childrenPaxCount: this.childrenPaxCount,
                              missingInternalValueError: this.missingInternalValueError,
                              onAdultCounterChange: this.onAdultCounterChange,
                              onChildrenCounterChange: this.onChildrenCounterChange,
                          })}
                      </div>
                  `
                : html`<div class="m-booking-request">
                      ${renderBookingRequestConfirmationTemplate({
                          supportPhone,
                          onBackClick: this.onBackButtonClick,
                      })}
                  </div>`}
        `;
    }
}

if (!customElements.get('der-tm-roundtrip-booking-request')) {
    customElements.define('der-tm-roundtrip-booking-request', RoundtripBookingRequest);
}

/**
 * Formats an ISO date string to `dd.mm.yy` in the `de-CH` locale.
 *
 * If the input is empty, returns an empty string.
 * If the input cannot be parsed as a date, returns the original value.
 *
 * @param isoDate ISO date string (or any string).
 * @returns Localized `dd.mm.yy` string, empty string, or the original input.
 */
function formatDateToDDMMYY(isoDate: string): string {
    if (!isoDate) return '';

    const parsedDate = new Date(isoDate);
    if (Number.isNaN(parsedDate.getTime())) return isoDate;

    return new Intl.DateTimeFormat('de-CH', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
    }).format(parsedDate);
}
